import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CalendarImport } from './components/CalendarImport.jsx';
import { ClientDirectory } from './components/ClientDirectory.jsx';
import { CopyDayImport } from './components/CopyDayImport.jsx';
import { ConfirmDialog } from './components/ConfirmDialog.jsx';
import { FullScreenConfetti } from './components/FullScreenConfetti.jsx';
import { HelpDialog } from './components/HelpDialog.jsx';
import { Icon } from './components/Icon.jsx';
import { JiraImport } from './components/JiraImport.jsx';
import { LoginScreen } from './components/LoginScreen.jsx';
import { LogTimeForm } from './components/LogTimeForm.jsx';
import { NotificationsView } from './components/NotificationsView.jsx';
import { PrinciplesModal } from './components/Principles.jsx';
import { ReportsView } from './components/ReportsView.jsx';
import { SubjectSwitcher } from './components/SubjectSwitcher.jsx';
import { SummaryView } from './components/SummaryView.jsx';
import { TeamView } from './components/TeamView.jsx';
import { TodayView } from './components/TodayView.jsx';
import { UserMenu } from './components/UserMenu.jsx';
import { ViewEntryDialog } from './components/ViewEntryDialog.jsx';
import { useToast } from './components/Toast.jsx';
import {
  SessionWarningBanner,
  SessionExpiredModal,
  SessionNetworkModal,
  ReauthOverlay,
  SessionRestoredBanner,
} from './components/SessionSurfaces.jsx';
import {
  api,
  setUnauthorizedHandler,
  setNetworkErrorHandler,
  setSessionTtlListener,
} from './lib/api.js';
import { addDays, fmtHM, fmtISO, fmtLong, fromISO } from './lib/format.js';
import {
  LOG_WINDOW_DAYS,
  canLoadOlder as canLoadOlderFor,
  daysToShow,
  isBeyondLogWindow,
  logWindowStart,
  maxViewDays as maxViewDaysFor,
  nextViewDays,
} from './lib/reviewWindow.js';
import { entryFormPrefill } from './lib/entryPrefill.js';
import {
  WARN_THRESHOLD_SECS,
  RESTORE_AUTOHIDE_MS,
  nextPhaseForTtl,
  remainingSeconds,
  persistSavedWork,
  readSavedWork,
  clearSavedWork,
  isReauthPending,
} from './lib/session.js';

const DAILY_TARGET_MIN = 8 * 60;
// The logging window (LOG_WINDOW_DAYS) lives in lib/reviewWindow.js: entries
// can only be created/edited within that many days (mirrored by LogTimeForm's
// date picker), while reviewing older entries is unbounded via the date bar's
// "Load older" control — those older days are shown read-only.
const totalMinutesOf = (entries) =>
  entries
    .filter((e) => !e.deleted)
    .reduce((s, e) => s + e.hours * 60 + e.minutes, 0);

// Easter-egg messages shown alongside the confetti when a day hits ≥8h.
// Mix of clean affirmations, name-personalized lines, and cheekier asides.
// `{name}` is replaced with the user's first name.
const CELEBRATION_MESSAGES = [
  'Eight hours. Clean.',
  'Day made.',
  'All in the books.',
  'Done and dusted.',
  "That's a wrap.",
  'Mission accomplished.',
  'Day shipped.',
  'Solid day.',
  'Calling it — well earned.',
  'Nice work, {name}.',
  '{name}, you absolute machine.',
  'Day made, {name}.',
  'Great hustle, {name}.',
  '{name} for the win.',
  'Touch grass time.',
  'Off-the-clock approved.',
  'You came, you saw, you logged.',
  'Hours: eight. Vibes: immaculate.',
  'Heroics noted.',
  'Closing the laptop? You should.',
  'Going the extra mile.',
  'Above and beyond.',
  'Look at you go.',
  'Showing off, are we?',
  'Clocked. Now go live a little.',
];

function pickCelebrationMessage(firstName) {
  const eligible = firstName
    ? CELEBRATION_MESSAGES
    : CELEBRATION_MESSAGES.filter((m) => !m.includes('{name}'));
  const raw = eligible[Math.floor(Math.random() * eligible.length)];
  return raw.replace('{name}', firstName || '');
}

function readLoginErrorFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  if (!error) return null;
  // Strip query so a refresh doesn't keep showing the error
  const url = new URL(window.location.href);
  url.search = '';
  window.history.replaceState({}, '', url.toString());
  return error;
}

const SELECTED_DATE_KEY = 'clockin:selectedDate';

// Restore the last-picked date for this tab session. The date is otherwise
// pure in-memory state, so any navigation that re-mounts the SPA — an import's
// full-page Calendar OAuth connect (which the server bounces back to `/`), a
// manual reload, or the browser back/forward buttons — silently snaps it back
// to today. Guarded: the stored value must be a real ISO date that isn't in
// the future and isn't past the sanity floor below; anything else falls back
// to today. A restored date older than the default 35-day window is allowed
// (v2.6 review) — the bar widens to show it. sessionStorage (not local) so a
// brand-new session or a fresh day still lands on today.
//
// The bar starts at the 35-day logging window and grows only as far as a
// restored date needs. This cap just rejects a wildly corrupt value so one bad
// string can't request a decades-long summary fetch; it's well beyond any real
// employment tenure the review floor would allow.
const PERSISTED_DATE_FLOOR_DAYS = 800;

function readPersistedDate(today) {
  try {
    const iso = window.sessionStorage.getItem(SELECTED_DATE_KEY);
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return today;
    const d = fromISO(iso);
    // fromISO builds `new Date(y, m-1, d)`, which normalizes impossible dates
    // (2026-02-31 → Mar 3) — so a format-valid but non-existent string would
    // slip past the regex. Round-trip through fmtISO to reject those, then
    // reject futures and anything older than the sanity floor.
    if (
      Number.isNaN(d.getTime()) ||
      fmtISO(d) !== iso ||
      d < addDays(today, -PERSISTED_DATE_FLOOR_DAYS) ||
      d > today
    ) {
      return today;
    }
    return d;
  } catch {
    return today;
  }
}

export default function App() {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const [bootState, setBootState] = useState({ status: 'loading' });
  const [selectedDate, setSelectedDate] = useState(() => readPersistedDate(today));
  // v2.6 — how many days the date bar shows, ending today. Starts at the
  // 35-day logging window and grows via "Load older" (or to fit a restored
  // review date) so past entries stay reviewable. Never shrinks in a session.
  const [viewDays, setViewDays] = useState(LOG_WINDOW_DAYS);
  const [entries, setEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dictionary, setDictionary] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [hideWeekends, setHideWeekends] = useState(() => {
    try {
      return window.localStorage.getItem('clockin:hideWeekends') !== 'false';
    } catch {
      return true;
    }
  });
  // Per-day total minutes for the days currently in the date-bar window.
  // Keyed by ISO date. Used by the date bar to color a dot on days the
  // user has already logged on.
  const [summaryByDate, setSummaryByDate] = useState({});
  // v2.0 — country-scoped holidays for the user's team. Fetched once at
  // boot and shared with Today (date-bar flag chips, empty state) and
  // Summary (working-day math). { country, byDate: Map<iso, holiday> }.
  // Server returns the static fallback when the sheet is unreachable, so
  // a fetch error here is genuinely surprising; we still tolerate it.
  const [holidayInfo, setHolidayInfo] = useState({
    country: '',
    byDate: new Map(),
  });
  const [loginVariant, setLoginVariant] = useState('default');
  const [loginEmail, setLoginEmail] = useState(null);
  // Bumped every time a save brings the day's total to ≥ 8h — drives the
  // full-screen confetti easter egg. The matching `confettiMessage` is
  // picked at the same moment so each celebration gets a fresh line.
  const [confettiKey, setConfettiKey] = useState(0);
  const [confettiMessage, setConfettiMessage] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [principlesOpen, setPrinciplesOpen] = useState(false);
  // v2.0 (A1) — first-session onboarding card on Today. Dismissed state is
  // persisted in localStorage per user (key includes the email), so a fresh
  // device or a logged-out re-login still surfaces it once. Default null
  // means "haven't read the flag yet" — we resolve it below in an effect
  // so SSR / first render doesn't flash the card.
  const [principlesCardDismissed, setPrinciplesCardDismissed] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [jiraOpen, setJiraOpen] = useState(false);
  const [copyDayOpen, setCopyDayOpen] = useState(false);
  // 'today' | 'summary' — Today is the hub; Summary is the read-only
  // monthly breakdown. Both are peer screens of the topbar tabs.
  const [screen, setScreen] = useState('today');
  // v2.0 (A4) — subject pivot for approvers / super-approvers.
  //   'self'          → own view (default; what employees always see)
  //   'team'          → Team view aggregate (only meaningful on Summary)
  //   <email>         → drilled into a report (read-only)
  const [subject, setSubject] = useState('self');
  // List of reports this approver can view; populated for approver /
  // super_approver roles after auth. Empty for plain employees so the
  // SubjectSwitcher renders nothing.
  const [reports, setReports] = useState([]);
  // v2.0 (A6) — read-only entry inspector when a row is in a locked month.
  const [viewEntry, setViewEntry] = useState(null);
  const toast = useToast();

  // ── Session-expiry experience (v2.4) ──────────────────────────────
  // A single phase drives the four surfaces on top of the engineering
  // baseline (401 → handler, re-check on tab focus). See lib/session.js.
  //   active | warning | expired | reauth | restored | network
  const [sessionPhase, setSessionPhase] = useState('active');
  const [savedWork, setSavedWork] = useState(null);
  const [warnSecs, setWarnSecs] = useState(WARN_THRESHOLD_SECS);
  const [reauthStage, setReauthStage] = useState('redirecting');
  const [netRetrying, setNetRetrying] = useState(false);
  // Drafts handed to the Jira import on the post-OAuth boot to rehydrate.
  const [jiraRestoredDrafts, setJiraRestoredDrafts] = useState(null);
  // Resolved once, at first render: is this load a return from the re-auth
  // redirect? Drives the "signing you back in…" overlay during bootstrap.
  const [returningFromReauth] = useState(() => isReauthPending());
  // Latest-value refs so the once-registered fetch handlers + the countdown
  // interval always see current state without re-subscribing every render.
  const phaseRef = useRef('active');
  const bootStatusRef = useRef('loading');
  const captureRef = useRef(() => null);
  // The live Jira draft snapshot, kept off-render so expiry can read it
  // synchronously (avoids the stale-closure trap the prototype hit).
  const jiraSnapshotRef = useRef(null);
  // { secs, at } — the most recent server TTL and when we synced it.
  const ttlSyncRef = useRef(null);
  phaseRef.current = sessionPhase;

  // Hide the "Saved" pill ~2.4s after the last successful write.
  useEffect(() => {
    if (!savedAt) return undefined;
    const id = setTimeout(() => setSavedAt(null), 2400);
    return () => clearTimeout(id);
  }, [savedAt]);

  const isAuthed = bootState.status === 'authed';
  bootStatusRef.current = bootState.status;

  // ── v2.6 review window ─────────────────────────────────────────────
  // The date bar can page back past the 35-day logging window so a user can
  // review older entries (e.g. to reconcile a month's total). Logging stays
  // capped at 35 days: older days render read-only.
  //
  // reviewFloor — how far back paging may reach. Employment start (enabled_on)
  // when known (you can't have logged before you existed); otherwise unbounded.
  const reviewFloor = useMemo(() => {
    const iso = bootState.user?.enabledOn;
    if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
    const d = fromISO(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  }, [bootState.user?.enabledOn]);
  // Days older than the logging window are review-only (kept in sync with
  // LogTimeForm's date picker — both derive from LOG_WINDOW_DAYS). See
  // lib/reviewWindow.js.
  const selectedBeyondLogWindow = isBeyondLogWindow(selectedDate, today);
  // Cap the bar at the floor (unbounded when there's none); never below 35.
  const maxViewDays = maxViewDaysFor(today, reviewFloor);
  const canLoadOlder = canLoadOlderFor(viewDays, maxViewDays);
  const loadOlder = useCallback(() => {
    // Reveal ~a month more per click, clamped to the review floor.
    setViewDays((n) => nextViewDays(n, 30, maxViewDays));
  }, [maxViewDays]);

  // A persisted / reauth-restored date can predate the review floor — which is
  // only known post-boot, so readPersistedDate can't check it — e.g. a stale
  // sessionStorage date from before an `enabledOn` correction. Snap it forward
  // so the bar never opens before employment start. The arrow-key path already
  // guards this for live navigation.
  useEffect(() => {
    if (reviewFloor && selectedDate < reviewFloor) setSelectedDate(reviewFloor);
  }, [reviewFloor, selectedDate]);

  // Keep the bar wide enough to show the selected day — covers a persisted or
  // reauth-restored review date older than the current window. Capped at
  // `maxViewDays` so a stale far-past date can't grow the bar past the review
  // ceiling. Arrow-key nav expands as it steps too; this is the catch-all.
  // Never shrinks.
  useEffect(() => {
    const needed = Math.min(daysToShow(selectedDate, today), maxViewDays);
    if (needed > viewDays) setViewDays(needed);
  }, [selectedDate, today, viewDays, maxViewDays]);

  const bootstrap = useCallback(async () => {
    const urlError = readLoginErrorFromUrl();
    try {
      const me = await api.me();
      const dict = await api.dictionary();
      setDictionary(dict);
      setBootState({
        status: 'authed',
        user: me.user,
        dailyHoursTarget: me.dailyHoursTarget,
        mockMode: me.mockMode,
        jiraConfigured: !!me.jiraConfigured,
        // v2.0 — months that are locked for *this* user. The Today
        // ribbon + disabled Log time button + view-only entry rows
        // all read off this list. Empty for unapproved users.
        lockedMonths: me.locked?.months || [],
      });
      // v2.4 — returning from the re-auth redirect: rehydrate the saved draft
      // and land back on the surface + date the user was on. Gated on the
      // "reauth pending" flag so an unrelated later login never restores a
      // stale draft. Jira is the wired hero (row-level restore); the others
      // restore the view (date) they were on.
      const saved = isReauthPending() ? readSavedWork() : null;
      if (saved) {
        if (saved.date) setSelectedDate(fromISO(saved.date));
        if (saved.kind === 'jira') {
          setJiraRestoredDrafts(saved.drafts || {});
          setJiraOpen(true);
        }
        setSavedWork(saved);
        setSessionPhase('restored');
      }
      clearSavedWork();
    } catch (err) {
      if (err.code === 'UNAUTH') {
        let mock = false;
        try {
          const h = await fetch('/healthz').then((r) => r.json());
          mock = !!h.mockMode;
        } catch {
          /* ignore */
        }
        setBootState({ status: 'unauthed', mockMode: mock });
        if (urlError === 'not_provisioned') setLoginVariant('not-provisioned');
      } else {
        setBootState({ status: 'error', error: err.message });
      }
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // v2.4 — capture whatever draft is open *synchronously* so a session lapse
  // can preserve it before we redirect. Jira reads its live rows from the
  // off-render snapshot ref (and reports the same ready/incomplete counts the
  // footer shows); the other surfaces restore the view they were on.
  const captureSavedWork = useCallback(() => {
    const date = fmtISO(selectedDate);
    const dateLabel = fmtLong(selectedDate);
    if (jiraOpen) {
      const snap =
        jiraSnapshotRef.current || { drafts: {}, rows: 0, ready: 0, incomplete: 0 };
      return {
        kind: 'jira',
        date,
        dateLabel,
        rows: snap.rows,
        ready: snap.ready,
        incomplete: snap.incomplete,
        drafts: snap.drafts,
      };
    }
    if (formOpen) return { kind: 'form', date, dateLabel, rows: 1 };
    if (calendarOpen) return { kind: 'calendar', date, dateLabel, rows: 1 };
    if (copyDayOpen) return { kind: 'copyday', date, dateLabel, rows: 1 };
    return null;
  }, [jiraOpen, formOpen, calendarOpen, copyDayOpen, selectedDate]);
  captureRef.current = captureSavedWork;

  // Flip to the blocking expired modal, snapshotting any open draft first.
  // Guarded so neither a stray 401 nor the countdown can yank the user out
  // of an already-open expiry / re-auth flow.
  const triggerExpiry = useCallback((prebuilt) => {
    if (phaseRef.current !== 'active' && phaseRef.current !== 'warning') return;
    const work = prebuilt && prebuilt.kind ? prebuilt : captureRef.current();
    setSavedWork(work || null);
    setSessionPhase('expired');
  }, []);

  // "Stay signed in" — ping the server (slides the sliding window) and re-read
  // the fresh TTL, then drop back to active without any redirect.
  const keepAlive = useCallback(async () => {
    let extended = false;
    try {
      const data = await api.sessionTtl();
      if (data && Number.isFinite(data.ttlSeconds)) {
        ttlSyncRef.current = { secs: data.ttlSeconds, at: Date.now() };
        setWarnSecs(data.ttlSeconds);
        extended = true;
      }
    } catch {
      /* a 401 here routes through the expiry handler; ignore otherwise */
    }
    // Only clear the warning + claim success when the window actually slid —
    // a failed keep-alive must not falsely reassure a still-expiring session.
    if (extended) {
      setSessionPhase((p) => (p === 'warning' ? 'active' : p));
      toast('Session extended — you’re good for another 60 minutes.');
    } else {
      toast('Could not extend session — please try again.', 'error');
    }
  }, [toast]);

  // "Sign back in with Google" — persist the draft + a returning flag, then do
  // the real full-page OAuth redirect (no popup, no new tab). The brief delay
  // lets the overlay paint before we hand off.
  const beginReauth = useCallback(() => {
    persistSavedWork(savedWork);
    setReauthStage('redirecting');
    setSessionPhase('reauth');
    window.setTimeout(() => {
      window.location.assign('/auth/google');
    }, 450);
  }, [savedWork]);

  // Network modal "Retry now" — probe connectivity (unauthenticated, cheap).
  const retryNetwork = useCallback(async () => {
    setNetRetrying(true);
    try {
      const res = await fetch('/healthz', { credentials: 'include' });
      if (!res.ok) throw new Error('still down');
      setSessionPhase((p) => (p === 'network' ? 'active' : p));
      toast('Reconnected — back online.');
    } catch {
      /* still unreachable — keep the modal up so the user can retry again */
    } finally {
      setNetRetrying(false);
    }
  }, [toast]);

  const handleJiraDrafts = useCallback((snap) => {
    jiraSnapshotRef.current = snap;
  }, []);

  // Register the fetch-layer handlers once. The 401 path now opens the
  // graceful expired modal (was: hard redirect to login) — but only when we
  // were actually authed; at boot a 401 still falls through to the login
  // screen via bootstrap's own catch. A connectivity/5xx failure opens the
  // distinct "can't reach ClockIn" modal, never the sign-out path.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (bootStatusRef.current === 'authed') triggerExpiry();
    });
    setNetworkErrorHandler(() => {
      if (bootStatusRef.current === 'authed' && phaseRef.current === 'active') {
        setSessionPhase('network');
      }
    });
    setSessionTtlListener((secs) => {
      ttlSyncRef.current = { secs, at: Date.now() };
    });
    return () => {
      setUnauthorizedHandler(null);
      setNetworkErrorHandler(null);
      setSessionTtlListener(null);
    };
  }, [triggerExpiry]);

  // Proactive countdown. Ticks the last server TTL down client-side; crossing
  // the warn threshold raises the nudge, a fresh (slid) TTL lowers it again,
  // and zero trips the expired modal. Only re-renders near/in the warning, so
  // a healthy session pays nothing.
  useEffect(() => {
    if (!isAuthed) return undefined;
    const id = setInterval(() => {
      const sync = ttlSyncRef.current;
      if (!sync || sync.secs == null) return;
      const remaining = remainingSeconds(sync.secs, sync.at, Date.now());
      const phase = phaseRef.current;
      const next = nextPhaseForTtl(phase, remaining);
      if (next === 'expired') {
        triggerExpiry();
        return;
      }
      if (next !== phase) setSessionPhase(next);
      if (next === 'warning') setWarnSecs(remaining);
    }, 1000);
    return () => clearInterval(id);
  }, [isAuthed, triggerExpiry]);

  // "Welcome back" banner auto-dismisses after a beat.
  useEffect(() => {
    if (sessionPhase !== 'restored') return undefined;
    const id = setTimeout(
      () => setSessionPhase((p) => (p === 'restored' ? 'active' : p)),
      RESTORE_AUTOHIDE_MS,
    );
    return () => clearTimeout(id);
  }, [sessionPhase]);

  // Sessions expire on inactivity; a backgrounded tab won't notice until
  // its next request 401s, leaving the UI looking live while every call
  // fails (the "nothing imported from Jira" symptom). Re-verify on
  // foreground so a stale tab bounces to login proactively. api.me()'s own
  // 401 trips the handler above; other errors are non-fatal here.
  useEffect(() => {
    if (!isAuthed) return;
    const recheck = () => {
      if (document.visibilityState === 'visible') {
        api.me().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', recheck);
    return () => {
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, [isAuthed]);

  useEffect(() => {
    try {
      window.localStorage.setItem('clockin:hideWeekends', hideWeekends ? 'true' : 'false');
    } catch {
      /* private mode or storage disabled — non-fatal */
    }
  }, [hideWeekends]);

  // Persist the picked date for this tab session so navigating away and back —
  // an import's full-page OAuth connect, a reload, or the browser back button —
  // restores it instead of resetting to today. See readPersistedDate.
  useEffect(() => {
    try {
      window.sessionStorage.setItem(SELECTED_DATE_KEY, fmtISO(selectedDate));
    } catch {
      /* private mode or storage disabled — non-fatal */
    }
  }, [selectedDate]);

  // Resolve the onboarding card's dismissed flag once we know who's logged
  // in. Keyed by lowercase email — sharing a workstation with a different
  // account still gets a clean first-session experience.
  useEffect(() => {
    if (bootState.status !== 'authed') return;
    const email = (bootState.user?.email || '').toLowerCase();
    if (!email) return;
    try {
      const v = window.localStorage.getItem(
        `clockin:principlesDismissed:${email}`,
      );
      setPrinciplesCardDismissed(v === '1');
    } catch {
      setPrinciplesCardDismissed(false);
    }
  }, [bootState]);

  const dismissPrinciplesCard = useCallback(() => {
    setPrinciplesCardDismissed(true);
    const email = (bootState.user?.email || '').toLowerCase();
    if (!email) return;
    try {
      window.localStorage.setItem(
        `clockin:principlesDismissed:${email}`,
        '1',
      );
    } catch {
      /* private mode or storage disabled — non-fatal */
    }
  }, [bootState]);

  const loadEntries = useCallback(
    async (date) => {
      if (!isAuthed) return;
      setEntriesLoading(true);
      try {
        // v2.0 (A4) — drill-in: fetch entries for the impersonated user
        // when the approver has selected a report from the switcher.
        const data =
          subject !== 'self' && subject !== 'team'
            ? await api.entriesFor(subject, fmtISO(date))
            : await api.entries(fmtISO(date));
        setEntries(data.entries || []);
      } catch (err) {
        // v2.4 — a 401 is owned by the global session-expiry handler (it opens
        // the graceful "session timed out" modal); don't also flip to login.
        if (err.code === 'UNAUTH') return;
        toast('Could not load entries', 'error');
      } finally {
        setEntriesLoading(false);
      }
    },
    [isAuthed, subject, toast],
  );

  useEffect(() => {
    if (isAuthed) loadEntries(selectedDate);
  }, [isAuthed, selectedDate, loadEntries]);

  // Per-day totals for the date bar dots, across the whole visible window.
  // Cheaper than refetching per-day entries, and refreshed after every
  // successful write so the dot stays in sync with what the user just did.
  // The range widens with `viewDays` so days revealed by "Load older" get
  // their dots too.
  const loadSummary = useCallback(async () => {
    if (!isAuthed) return;
    const from = fmtISO(logWindowStart(today, viewDays));
    const to = fmtISO(today);
    try {
      const data = await api.entriesSummary(from, to);
      const map = {};
      for (const d of data.days || []) map[d.date] = d.totalMin;
      setSummaryByDate(map);
    } catch (err) {
      // v2.4 — 401 handled by the global session-expiry handler. Any other
      // error is non-fatal here: the date-bar dots just won't appear.
      void err;
    }
  }, [isAuthed, today, viewDays]);

  useEffect(() => {
    if (isAuthed) loadSummary();
  }, [isAuthed, loadSummary]);

  // v2.0 (A4) — load the approver's report list once after auth. The
  // server returns 403 for plain employees; treat that as an empty
  // report list rather than an error so the rest of the app keeps
  // working. The fetch happens before any subject switch so the
  // switcher renders immediately.
  useEffect(() => {
    if (!isAuthed) return;
    const role = bootState.user?.role;
    if (role !== 'approver' && role !== 'super_approver') {
      setReports([]);
      return;
    }
    let cancelled = false;
    api
      .teamReports()
      .then((data) => {
        if (!cancelled) setReports(data.reports || []);
      })
      .catch(() => {
        if (!cancelled) setReports([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthed, bootState.user?.role]);

  // v2.0 (A4) — keep subject + screen consistent. "Team view" only
  // exists in the Summary tab; if the user clicks back to Today while
  // on team view, snap them out of team view so they don't see a blank
  // Today.
  useEffect(() => {
    if (subject === 'team' && screen === 'today') setSubject('self');
  }, [subject, screen]);

  // v2.4 — Clients tab is approver / super-approver only. If the role
  // drops to plain employee while the directory is open (or a stale
  // session restores it), snap back to Today rather than render an
  // unauthorised view shell.
  useEffect(() => {
    if (screen !== 'clients') return;
    const role = bootState.user?.role;
    if (role !== 'approver' && role !== 'super_approver') setScreen('today');
  }, [screen, bootState.user?.role]);

  // Reports tab is super-approver only. If the role drops below that while
  // Reports is open (or a stale session restores it), snap back to Today
  // rather than render an unauthorised shell. Reports is never the default
  // screen — Today remains the landing.
  useEffect(() => {
    if (screen !== 'reports') return;
    if (bootState.user?.role !== 'super_approver') setScreen('today');
  }, [screen, bootState.user?.role]);

  // v2.0 — load the user's country holidays once after auth. Holidays
  // rarely change so a single fetch per session is fine; the server
  // caches for 5 minutes anyway. Failure is non-fatal — the flag chips
  // just won't render, and Summary falls back to weekday-only math.
  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.holidays();
        if (cancelled) return;
        const byDate = new Map();
        (data.holidays || []).forEach((h) => byDate.set(h.date, h));
        setHolidayInfo({ country: data.country || '', byDate });
      } catch {
        // intentional silence — see comment above
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  // Global keyboard shortcuts — only active on the Today view. The form
  // owns its own Esc/⌘+Enter handlers; we bail out when anything modal-y
  // is open or focus is in an input. `?` is Shift+/ on US layouts; ev.key
  // resolves to '?' directly so no need to inspect modifiers.
  useEffect(() => {
    if (!isAuthed) return undefined;
    const onKey = (ev) => {
      const target = ev.target;
      const tag = (target?.tagName || '').toLowerCase();
      const isTyping =
        tag === 'input' ||
        tag === 'textarea' ||
        tag === 'select' ||
        target?.isContentEditable;
      const modalOpen = formOpen || helpOpen || !!confirm;
      // Allow `?` to toggle help even when typing? No — too disruptive.
      if (isTyping) return;
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;

      // Esc closes the help dialog (form/confirm handle their own).
      if (ev.key === 'Escape') {
        if (helpOpen) {
          setHelpOpen(false);
          ev.preventDefault();
        }
        return;
      }

      // Don't fire other shortcuts while a modal/dialog is up.
      if (modalOpen) return;

      // ? (help) is global; the rest only make sense on Today.
      if (ev.key === '?') {
        setHelpOpen(true);
        ev.preventDefault();
        return;
      }
      if (screen !== 'today') return;

      if (ev.key === 'n' || ev.key === 'N') {
        // Older-than-35-days is review-only — no new entry there.
        if (selectedBeyondLogWindow) return;
        setEditingEntry(null);
        setFormOpen(true);
        ev.preventDefault();
      } else if (ev.key === 't' || ev.key === 'T') {
        setSelectedDate(today);
        ev.preventDefault();
      } else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight') {
        // Step one day, skipping weekends when the toggle is on. Don't walk
        // past today; don't walk past the review floor (employment start).
        // Stepping left past the visible edge grows the bar so older days
        // stay reachable — reviewing is unbounded even though logging isn't.
        const dir = ev.key === 'ArrowLeft' ? -1 : 1;
        let next = addDays(selectedDate, dir);
        if (hideWeekends) {
          let guard = 0;
          while ((next.getDay() === 0 || next.getDay() === 6) && guard < 7) {
            next = addDays(next, dir);
            guard += 1;
          }
        }
        if (next > today) return;
        if (reviewFloor && next < reviewFloor) return;
        // Don't step past the review ceiling (guards the no-floor case, where
        // maxViewDays falls back to the absolute cap rather than Infinity).
        const needed = daysToShow(next, today);
        if (needed > maxViewDays) return;
        // Grow the bar if we stepped past its left edge so the day stays shown.
        if (needed > viewDays) setViewDays(needed);
        setSelectedDate(next);
        ev.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    isAuthed,
    formOpen,
    helpOpen,
    confirm,
    selectedDate,
    hideWeekends,
    today,
    screen,
    viewDays,
    maxViewDays,
    reviewFloor,
    selectedBeyondLogWindow,
  ]);

  // Pre-fill a *new* entry from the day's most-recent entry (Activity only —
  // Product is a deliberate pick each time). See lib/entryPrefill.js.
  const prefill = useMemo(() => entryFormPrefill(entries), [entries]);

  const handleSave = async (data, opts = {}) => {
    setSaving(true);
    try {
      let savedEntry = null;
      let nextEntries = entries;
      if (editingEntry) {
        const res = await api.updateEntry(editingEntry.id, data);
        savedEntry = res.entry;
        nextEntries = entries.map((e) =>
          e.id === editingEntry.id ? res.entry : e,
        );
        setEntries(nextEntries);
        toast(
          `Entry updated — ${fmtHM(data.hours * 60 + data.minutes)} logged`,
        );
      } else {
        const res = await api.createEntry(data);
        savedEntry = res.entry;
        if (res.entry.date === fmtISO(selectedDate)) {
          nextEntries = [...entries, res.entry];
          setEntries(nextEntries);
        }
        toast(
          opts.andContinue
            ? `Entry saved — add another`
            : `Entry saved — ${fmtHM(data.hours * 60 + data.minutes)} logged`,
        );
      }
      setSavedAt(Date.now());
      // andContinue: keep the form open so the user can log another
      // entry; the form itself clears its fields and surfaces a banner.
      if (!opts.andContinue) {
        setFormOpen(false);
        setEditingEntry(null);
      }
      // Refresh the day-total summary so the date-bar dots reflect the
      // entry we just saved (esp. when saving for a different day).
      loadSummary();
      // Easter egg: full-screen confetti + a random cheerful message
      // every time a save brings the viewed day's total to 8h or above.
      // Only fires when the saved entry is for the selected date — that's
      // the day we have full entry data for in memory, and it's where
      // the user's eyes are.
      if (savedEntry && savedEntry.date === fmtISO(selectedDate)) {
        const dayMins = totalMinutesOf(
          nextEntries.filter((e) => e.date === fmtISO(selectedDate)),
        );
        if (dayMins >= DAILY_TARGET_MIN) {
          const firstName = bootState.user?.name?.split(' ')?.[0] || '';
          setConfettiMessage(pickCelebrationMessage(firstName));
          setConfettiKey((k) => k + 1);
        }
      }
    } catch (err) {
      if (err.status === 503) {
        toast('Could not save — please try again', 'error');
      } else if (err.code === 'UNAUTH') {
        // v2.4 — the global session-expiry handler shows the modal; the form
        // stays open underneath so the draft is preserved.
      } else {
        toast('Could not save entry', 'error');
      }
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (entry) => {
    setConfirm({
      title: 'Delete this entry?',
      body: `Remove “${entry.activityName}” (${fmtHM(
        entry.hours * 60 + entry.minutes,
      )}). You can re-create it from the form.`,
      confirmLabel: 'Delete entry',
      danger: true,
      onConfirm: async () => {
        setSaving(true);
        try {
          await api.deleteEntry(entry.id);
          setEntries((es) => es.filter((e) => e.id !== entry.id));
          toast('Entry deleted', 'error');
          setSavedAt(Date.now());
          loadSummary();
        } catch (err) {
          if (err.status === 503) {
            toast('Could not delete — please try again', 'error');
          } else {
            toast('Could not delete entry', 'error');
          }
        } finally {
          setSaving(false);
          setConfirm(null);
        }
      },
    });
  };

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    setBootState({ status: 'unauthed', mockMode: bootState.mockMode });
    setEntries([]);
  };

  const handleMockLogin = async () => {
    try {
      await api.mockLogin();
      bootstrap();
    } catch {
      toast('Could not sign in', 'error');
    }
  };

  if (bootState.status === 'loading') {
    // v2.4 — if we're booting back from the re-auth redirect, show stage 2 of
    // the hand-off ("Signing you back in…") instead of a bare spinner while
    // the SPA re-bootstraps and the draft rehydrates.
    if (returningFromReauth) {
      return <ReauthOverlay stage="returning" user={{}} work={readSavedWork()} />;
    }
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--sp-fg-3)',
        }}
      >
        Loading…
      </div>
    );
  }

  if (bootState.status === 'unauthed') {
    return (
      <LoginScreen
        variant={loginVariant}
        mockMode={!!bootState.mockMode}
        errorEmail={loginEmail}
        onMockLogin={handleMockLogin}
      />
    );
  }

  const networkBusy = entriesLoading || saving;

  return (
    <div className="tt-shell">
      {/* Top-of-page sliver while any network op is in flight */}
      {networkBusy && <div className="tt-net-bar" />}

      <header className="tt-topbar" style={{ gap: 9, padding: '0 18px' }}>
        <a
          className="tt-wordmark"
          href="#"
          onClick={(e) => e.preventDefault()}
        >
          <img
            src="/swiftpay/SwiftPay-logo-black.svg"
            style={{ height: 22 }}
          />
          <span className="tt-product-tag">ClockIn</span>
        </a>
        <nav style={{ marginLeft: 6, display: 'flex', gap: 2, flexShrink: 0 }}>
          {[
            { k: 'today', label: 'Today', icon: 'clock' },
            { k: 'summary', label: 'Summary', icon: 'chart' },
            // Reports — org-wide client analytics. Super-approver only;
            // hidden for approvers + employees and gated server-side too.
            // Sits between Summary and Clients.
            ...(bootState.user?.role === 'super_approver'
              ? [{ k: 'reports', label: 'Reports', icon: 'pie' }]
              : []),
            // v2.4 — Clients directory is approver + super-approver only.
            // Plain employees never see the tab and the POST is gated
            // server-side as a backstop.
            ...(bootState.user?.role === 'approver' ||
            bootState.user?.role === 'super_approver'
              ? [{ k: 'clients', label: 'Clients', icon: 'users' }]
              : []),
            // v2.0 (B7 admin) — Notifications surface is super-approver only.
            // Gated upstream so plain employees + approvers never see it.
            ...(bootState.user?.role === 'super_approver'
              ? [{ k: 'notifications', label: 'Notifications', icon: 'mail' }]
              : []),
          ].map((tab) => {
            const active = screen === tab.k;
            return (
              <button
                key={tab.k}
                onClick={() => setScreen(tab.k)}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '7px 11px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  color: active ? 'var(--sp-fg-1)' : 'var(--sp-fg-3)',
                  background: active ? 'var(--sp-bg-page)' : 'transparent',
                  transition: 'background .12s, color .12s',
                }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.color = 'var(--sp-fg-1)';
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.color = 'var(--sp-fg-3)';
                }}
              >
                <Icon name={tab.icon} size={14} />
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* v2.5 — Subject Switcher lives in the top bar, immediately right of
            the page-nav tabs and divided from them by a hairline. It only
            renders for non-employee roles, and only on the screens where a
            subject pivot is meaningful (Today / Summary) — hidden on Clients
            and Notifications. flex-shrink: 0 + single-line labels keep it from
            wrapping or pushing the user menu off the edge. */}
        {bootState.user?.role !== 'employee' &&
          (screen === 'today' || screen === 'summary') && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexShrink: 0,
                paddingLeft: 10,
                borderLeft: '1px solid var(--sp-border)',
              }}
            >
              <SubjectSwitcher
                subject={subject}
                role={bootState.user?.role}
                reports={reports}
                onChange={(next) => {
                  // Switching to a non-self subject means we should also
                  // close any open Log time form — drill-in is read-only.
                  if (next !== 'self') {
                    setFormOpen(false);
                    setEditingEntry(null);
                  }
                  setSubject(next);
                  // Team view only makes sense on Summary; auto-jump.
                  if (next === 'team') setScreen('summary');
                }}
              />
            </div>
          )}

        <div style={{ flex: 1, minWidth: 12 }} />

        {/* Subtle network status — only when something is happening or just finished */}
        {networkBusy && (
          <span className="tt-status-pill" style={{ marginRight: 8 }}>
            <span className="tt-spinner sm" />
            {saving ? 'Saving…' : 'Refreshing data…'}
          </span>
        )}
        {!networkBusy && savedAt && (
          <span
            className="tt-status-pill is-success"
            style={{ marginRight: 8 }}
          >
            <Icon name="check" size={12} stroke={2.6} />
            Saved
          </span>
        )}

        {bootState.mockMode && (
          <span
            className="tt-product-tag"
            style={{ marginRight: 8, color: '#C4320A' }}
          >
            mock data
          </span>
        )}
        <UserMenu
          user={bootState.user}
          onLogout={handleLogout}
          onShowShortcuts={() => setHelpOpen(true)}
          onShowPrinciples={() => setPrinciplesOpen(true)}
        />
      </header>

      <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
      <PrinciplesModal
        open={principlesOpen}
        onClose={() => setPrinciplesOpen(false)}
      />

      {screen === 'notifications' &&
      bootState.user?.role === 'super_approver' ? (
        <NotificationsView reports={reports} />
      ) : screen === 'reports' &&
        bootState.user?.role === 'super_approver' ? (
        <ReportsView />
      ) : screen === 'clients' &&
        (bootState.user?.role === 'approver' ||
          bootState.user?.role === 'super_approver') ? (
        <ClientDirectory />
      ) : screen === 'summary' && subject === 'team' ? (
        <TeamView
          role={bootState.user?.role}
          onDrillIn={(email) => {
            setSubject(email);
          }}
        />
      ) : screen === 'summary' ? (
        <SummaryView
          dailyHoursTarget={bootState.dailyHoursTarget || 8}
          holidays={holidayInfo}
          subjectEmail={subject !== 'self' ? subject : null}
          subjectName={
            subject !== 'self'
              ? reports.find((r) => r.email === subject)?.name
              : null
          }
          // v2.6 — optional-logging treatment for an excluded user, whether
          // it's their own Summary (self) or an approver drilling into one.
          exempt={
            subject !== 'self'
              ? !!reports.find((r) => r.email === subject)?.loggingExempt
              : !!bootState.user?.loggingExempt
          }
          // v2.0 follow-up — pass the caller's active range so the
          // hero card can prorate expected hours when they're a
          // partial-month employee. Drill-in (subjectEmail set) keeps
          // the existing full-month math; the server-side /api/team
          // path is the source of truth for those rows.
          userEnabledOn={bootState.user?.enabledOn || ''}
          userDisabledOn={bootState.user?.disabledOn || ''}
        />
      ) : (
        <TodayView
          entries={entries}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          onLog={(entry) => {
            if (subject !== 'self') return; // read-only in drill-in
            setEditingEntry(entry);
            setFormOpen(true);
          }}
          onEdit={(entry) => {
            if (subject !== 'self') return;
            setEditingEntry(entry);
            setFormOpen(true);
          }}
          onView={(entry) => setViewEntry(entry)}
          onDelete={subject === 'self' ? handleDelete : undefined}
          onImportCalendar={
            subject === 'self' ? () => setCalendarOpen(true) : undefined
          }
          onImportJira={
            subject === 'self' && bootState.jiraConfigured
              ? () => setJiraOpen(true)
              : undefined
          }
          onImportCopyDay={
            subject === 'self' ? () => setCopyDayOpen(true) : undefined
          }
          user={bootState.user}
          dailyHoursTarget={bootState.dailyHoursTarget || 8}
          hideWeekends={hideWeekends}
          onToggleHideWeekends={setHideWeekends}
          loading={entriesLoading}
          summaryByDate={summaryByDate}
          viewDays={viewDays}
          onLoadOlder={loadOlder}
          canLoadOlder={canLoadOlder}
          beyondLogWindow={selectedBeyondLogWindow}
          lockedMonths={bootState.lockedMonths || []}
          readOnlySubject={
            subject !== 'self'
              ? reports.find((r) => r.email === subject)
              : null
          }
          // v2.6 — optional-logging treatment: own Today when the user is
          // excluded, or the approver's drill-in into an excluded report.
          exempt={
            subject !== 'self'
              ? !!reports.find((r) => r.email === subject)?.loggingExempt
              : !!bootState.user?.loggingExempt
          }
          showPrinciplesCard={!principlesCardDismissed}
          onDismissPrinciplesCard={dismissPrinciplesCard}
          onOpenPrinciples={() => setPrinciplesOpen(true)}
          holidays={holidayInfo}
        />
      )}

      <CalendarImport
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        user={bootState.user}
        selectedDate={selectedDate}
        dictionary={dictionary}
        onImported={(count) => {
          // Refresh entries + summary so what we just imported shows up.
          loadEntries(selectedDate);
          loadSummary();
          toast(
            `Imported ${count} entr${count === 1 ? 'y' : 'ies'} from Calendar`,
          );
          setCalendarOpen(false);
        }}
      />

      <JiraImport
        open={jiraOpen}
        onClose={() => {
          setJiraOpen(false);
          setJiraRestoredDrafts(null);
          // Drop the stale snapshot so a lapse before the next emit can't
          // capture drafts from the just-closed session.
          jiraSnapshotRef.current = null;
        }}
        user={bootState.user}
        selectedDate={selectedDate}
        dictionary={dictionary}
        // v2.4 — session-expiry seams: observe the live draft so a lapse can
        // snapshot it, and seed from the preserved draft on the post-OAuth boot.
        restoredDrafts={jiraRestoredDrafts}
        onDraftsChange={handleJiraDrafts}
        onImported={(count) => {
          loadEntries(selectedDate);
          loadSummary();
          toast(
            `Imported ${count} entr${count === 1 ? 'y' : 'ies'} from Jira`,
          );
          setJiraOpen(false);
          setJiraRestoredDrafts(null);
          jiraSnapshotRef.current = null;
        }}
      />

      <CopyDayImport
        open={copyDayOpen}
        onClose={() => setCopyDayOpen(false)}
        targetDate={selectedDate}
        allEntries={entries}
        onCopy={async (copies) => {
          for (const copy of copies) {
            const res = await api.createEntry(copy);
            if (res.entry?.date === fmtISO(selectedDate)) {
              setEntries((es) => [...es, res.entry]);
            }
          }
          loadEntries(selectedDate);
          loadSummary();
          toast(
            `Copied ${copies.length} entr${copies.length === 1 ? 'y' : 'ies'} from a previous week`,
          );
          setCopyDayOpen(false);
        }}
      />

      {formOpen && dictionary && (
        <LogTimeForm
          mode={editingEntry ? 'edit' : 'create'}
          initial={editingEntry}
          user={bootState.user}
          dictionary={dictionary}
          onSave={handleSave}
          onCancel={() => {
            if (saving) return;
            setFormOpen(false);
            setEditingEntry(null);
          }}
          prefill={prefill}
          saving={saving}
          defaultDate={selectedDate}
        />
      )}

      <ConfirmDialog
        open={!!confirm}
        {...(confirm || {})}
        busy={saving}
        onCancel={() => {
          if (saving) return;
          setConfirm(null);
        }}
      />

      {/* v2.0 (A6) — read-only inspector for entries in a locked month. */}
      <ViewEntryDialog entry={viewEntry} onClose={() => setViewEntry(null)} />

      {/* Easter egg: confetti shower + cheerful message whenever a save
          brings the day to ≥ 8h. */}
      <FullScreenConfetti trigger={confettiKey} message={confettiMessage} />

      {/* ── Session-expiry surfaces (v2.4) ──────────────────────────────
          Rendered last so they stack above everything, incl. an open
          full-screen import (backdrop z-350 > import z-200). */}
      {sessionPhase === 'warning' && (
        <SessionWarningBanner
          secondsLeft={warnSecs}
          onStay={keepAlive}
          onDismiss={() => setSessionPhase('active')}
        />
      )}
      {sessionPhase === 'expired' && (
        <SessionExpiredModal
          work={savedWork}
          user={bootState.user}
          onReauth={beginReauth}
        />
      )}
      {sessionPhase === 'network' && (
        <SessionNetworkModal
          retrying={netRetrying}
          onRetry={retryNetwork}
          onDismiss={() => setSessionPhase('active')}
        />
      )}
      {sessionPhase === 'reauth' && (
        <ReauthOverlay stage={reauthStage} user={bootState.user} work={savedWork} />
      )}
      {sessionPhase === 'restored' && (
        <SessionRestoredBanner
          work={savedWork}
          user={bootState.user}
          onDismiss={() => setSessionPhase('active')}
        />
      )}
    </div>
  );
}
